import { MOTION_DURATION_MS } from "@/utils/motion";
import React, { useLayoutEffect, useRef } from "react";

// Tailwind's `ease-out`, which the transitions this continues use.
const TAILWIND_EASE_OUT = "cubic-bezier(0, 0, 0.2, 1)";

interface RecordedTransition {
  from: string;
  to: string;
  startedAt: number;
}

// The last transition each key began, kept across pages because each page mounts its own layout.
const recordedTransitions = new Map<string, RecordedTransition>();

/**
 * Carries a CSS transition across a page change. Each page mounts its own layout, so an element
 * still moving when the next page arrives would reappear at its end and skip the rest of the move.
 * The outgoing element records when its move began; the incoming one, mounting at that move's end
 * value, replays the remainder with the Web Animations API from where the outgoing one had got to.
 *
 * `value` is the element's current end value for `property`, as CSS text ("64px",
 * "translateX(-100%)"). Only changes made while `animated` record a move; a value that is placed
 * rather than transitioned leaves nothing to continue. The element's own transition must run
 * `durationMs` with Tailwind's `ease-out`.
 */
export const useTransitionAcrossPages = (
  key: string,
  ref: React.RefObject<HTMLElement | null>,
  property: "width" | "transform" | "paddingLeft",
  value: string,
  {
    animated = true,
    durationMs = MOTION_DURATION_MS.medium,
  }: { animated?: boolean; durationMs?: number } = {},
) => {
  // On mount, the element may be landing at the end of a move the outgoing page began.
  useLayoutEffect(() => {
    const element = ref.current;
    const recorded = recordedTransitions.get(key);
    // Without the Web Animations API (jsdom), the element simply lands where the move ends.
    if (element?.animate == null || recorded == null || recorded.to !== value) return;
    const elapsed = performance.now() - recorded.startedAt;
    if (elapsed >= durationMs) return;

    const animation = element.animate(
      [{ [property]: recorded.from }, { [property]: recorded.to }],
      { duration: durationMs, delay: -elapsed, easing: TAILWIND_EASE_OUT },
    );
    return () => animation.cancel();
    // Mount only: every later value is this element's own transition, which it records below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousValueRef = useRef(value);
  useLayoutEffect(() => {
    const previous = previousValueRef.current;
    previousValueRef.current = value;
    if (previous === value || !animated) return;
    recordedTransitions.set(key, { from: previous, to: value, startedAt: performance.now() });
  }, [animated, key, value]);
};
